import {
  Box,
  Button,
  createStandaloneToast,
  Flex,
  Text,
} from "@chakra-ui/react";
import GamelightTable from "components/table";
import { Column, FilterDefinition, Row } from "hooks/useTable";
import DateDisplay from "components/display/DateDisplay";
import AmountDisplay from "components/display/AmountDisplay";
import StatusDisplay from "components/display/StatusDisplay";
import { PROCESS_STATUS, PAYMENT_STATUS } from "constants/invoice";
import {
  getInvoices,
  updatePaymentStatus,
} from "views/admin/invoices/services";
import { InvoiceData } from "views/admin/invoices/types";
import useAdmin from "hooks/useAdmin";
import NewInvoiceModal, {
  NewInvoiceModalRef,
} from "views/admin/invoices/components/NewInvoiceModal";
import { useRef } from "react";
import GamelightButton from "components/button";
import { FaPlus } from "react-icons/fa";
import { TableContextProvider } from "contexts/TableContext";

const COLUMNS: Column<InvoiceData>[] = [
  { columnKey: "companyName", header: "COMPANY NAME" },
  {
    columnKey: "processStatus",
    header: "PROCESS STATUS",
    render: (data, updateItem) => (
      <StatusDisplay
        status={data.processStatus}
        configuration={PROCESS_STATUS}
        onUpdate={
          data.paymentStatus === "PAID"
            ? undefined
            : async (status) => {
                const toast = createStandaloneToast();
                try {
                  const response = await updatePaymentStatus({
                    ...data,
                    processStatus: status,
                  });

                  if (!response.success) {
                    toast({
                      title: "Error updating process status",
                      status: "error",
                      duration: 9000,
                      isClosable: true,
                    });
                    return;
                  }

                  updateItem("invoiceStatusId", data.invoiceStatusId, {
                    ...data,
                    processStatus: status,
                  });

                  toast({
                    title: "Process status updated",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                  });
                } catch (error) {
                  console.warn(error);
                }
              }
        }
        options={{
          UPLOADED: "UPLOADED",
          SENT: "SENT",
          SENDING_ERROR: "SENDING ERROR",
        }}
      />
    ),
  },
  {
    columnKey: "paymentStatus",
    header: "PAYMENT STATUS",
    render: (data, updateItem) => (
      <StatusDisplay
        status={data.paymentStatus}
        configuration={PAYMENT_STATUS}
        onUpdate={async (status) => {
          const toast = createStandaloneToast();
          try {
            const response = await updatePaymentStatus({
              ...data,
              paymentStatus: status,
            });

            if (!response.success) {
              toast({
                title: "Error updating payment status",
                status: "error",
                duration: 9000,
                isClosable: true,
              });
              return;
            }

            updateItem("invoiceStatusId", data.invoiceStatusId, {
              ...data,
              paymentStatus: status,
            });

            toast({
              title: "Payment status updated",
              status: "success",
              duration: 5000,
              isClosable: true,
            });
          } catch (error) {
            console.warn(error);
          }
        }}
        options={{ PAID: "PAID", OPEN: "OPEN" }}
      />
    ),
  },
  {
    columnKey: "amount",
    header: "AMOUNT",
    render: (data, updateItem) => (
      <AmountDisplay
        amount={data.amount}
        currencySymbol={data.currencySymbol ?? "$"}
        onUpdate={
          data.paymentStatus === "PAID"
            ? undefined
            : async (newAmount) => {
                const toast = createStandaloneToast();
                try {
                  const response = await updatePaymentStatus({
                    ...data,
                    amount: newAmount,
                  });

                  if (!response.success) {
                    toast({
                      title: "Error updating amount",
                      status: "error",
                      duration: 9000,
                      isClosable: true,
                    });
                    return;
                  }

                  updateItem("invoiceStatusId", data.invoiceStatusId, {
                    ...data,
                    amount: newAmount,
                  });

                  toast({
                    title: "Amount updated",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                  });
                } catch (error) {
                  console.warn(error);
                }
              }
        }
      />
    ),
  },
  {
    columnKey: "range",
    header: "DATE",
    render: (data) => <DateDisplay date={data.range} />,
  },
];

const FILTERS: FilterDefinition[] = [
  {
    label: "Payment Status",
    filterKey: "status",
    type: "selectbox",
    options: [
      {
        label: "PAID",
        value: "PAID",
      },
      {
        label: "OPEN",
        value: "OPEN",
      },
    ],
  },
  {
    label: "Company Name",
    filterKey: "companyName",
    type: "input",
  },
  {
    label: "Invoice Date",
    filterKey: "range",
    type: "date",
  },
  {
    filterKey: "isOverdue",
    defaultValue: true,
    type: "checkbox",
    label: "Show Overdue Invoices",
  },
];

const ROW_CONFIG: Row<InvoiceData> = {
  onClick(row) {},
  rowStyle(row) {
    return {
      bgColor: row.isOverdue ? "red.200" : undefined,
    };
  },
};

function Invoices() {
  const isAdmin = useAdmin();
  const modalRef = useRef<NewInvoiceModalRef>();

  if (!isAdmin) {
    return <></>;
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} borderRadius={8}>
      <TableContextProvider>
        <GamelightTable
          columns={COLUMNS}
          filters={FILTERS}
          fetchData={getInvoices}
          uniqueKey="invoiceStatusId"
          title="Invoices"
          rowConfig={ROW_CONFIG}
          buttons={[
            <GamelightButton
              variant={"ghost"}
              onClick={() => modalRef.current?.openDialog()}
            >
              <Flex alignItems={"center"} gap={2}>
                <FaPlus />
                <Text>New Invoice Tracker</Text>
              </Flex>
            </GamelightButton>,
          ]}
        >
          <NewInvoiceModal ref={modalRef} />
        </GamelightTable>
      </TableContextProvider>
    </Box>
  );
}

export default Invoices;
